/**
 * Analytics & Error Monitoring — loaded via CDN to avoid npm deps.
 * Replace DSN/API keys with real values from your dashboards.
 *
 * Sentry: https://sentry.io → Settings → Client Keys (DSN)
 * PostHog: https://posthog.com → Project Settings → API Key
 *
 * NOTE: dangerouslySetInnerHTML is safe here because all values are
 * server-side environment variables, never user input.
 */

// ─── Sentry Error Monitoring (CDN) ──────────────────────────

export function SentryScript() {
  const dsn = process.env.SENTRY_DSN ?? ''
  if (!dsn) return null

  // Safe: dsn is a server-side env var, not user input
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
(function(){
  // RGPD: load Sentry (incl. on-error session replay) only with analytics consent.
  try{var c=JSON.parse(localStorage.getItem('cookie-consent')||'{}');if(!(c&&c.analytics===true))return;}catch(e){return;}
  var s=document.createElement('script');
  s.src='https://browser.sentry-cdn.com/8.50.0/bundle.min.js';
  s.crossOrigin='anonymous';
  s.onload=function(){
    Sentry.init({
      dsn:'${dsn}',
      environment:'${process.env.NODE_ENV ?? 'production'}',
      tracesSampleRate:0.1,
      replaysSessionSampleRate:0,
      replaysOnErrorSampleRate:1.0,
    });
  };
  document.head.appendChild(s);
})();
`,
      }}
    />
  )
}

// ─── PostHog Analytics (CDN) ─────────────────────────────────

export function PostHogScript() {
  const apiKey = process.env.POSTHOG_API_KEY ?? ''
  if (!apiKey) return null

  // Safe: apiKey is a server-side env var, not user input
  return (
    <script
      dangerouslySetInnerHTML={{
        __html: `
(function(){try{var c=JSON.parse(localStorage.getItem('cookie-consent')||'{}');if(!(c&&c.analytics===true))return;}catch(e){return;}
!function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.crossOrigin="anonymous",p.async=!0,p.src="https://eu-assets.i.posthog.com/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="init capture register register_once register_for_session unregister unregister_for_session getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty createPersonProfile opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing debug".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
posthog.init('${apiKey}',{api_host:'https://eu.i.posthog.com',person_profiles:'identified_only'});
})();
`,
      }}
    />
  )
}

// ─── Custom Event Tracking Helper ────────────────────────────

/**
 * Track a custom event. Call from client components.
 * Events are sent to PostHog if loaded.
 *
 * Usage:
 *   trackEvent('quiz_completed', { category: 'plomberie', step: 6 })
 *   trackEvent('search_performed', { query: 'plombier', city: 'Paris' })
 */
export function trackEvent(event: string, properties?: Record<string, any>) {
  if (typeof window !== 'undefined' && (window as any).posthog) {
    ;(window as any).posthog.capture(event, properties)
  }
}

// ─── Pre-defined Event Names ─────────────────────────────────

export const EVENTS = {
  QUIZ_STARTED: 'quiz_started',
  QUIZ_STEP_COMPLETED: 'quiz_step_completed',
  QUIZ_SUBMITTED: 'quiz_submitted',
  SEARCH_PERFORMED: 'search_performed',
  SEARCH_FILTER_APPLIED: 'search_filter_applied',
  GEOLOCATION_USED: 'geolocation_used',
  PRO_PROFILE_VIEWED: 'pro_profile_viewed',
  REVIEW_SUBMITTED: 'review_submitted',
  USER_REGISTERED: 'user_registered',
  PRO_REGISTERED: 'pro_registered',
  PLAN_SELECTED: 'plan_selected',
} as const
